import { useTranslation } from 'react-i18next';
import { useAppState } from 'context/AppState';
import {
  StyledRow,
  StyledCol,
  StyledH1,
  AgronomicInputsContainer,
  StyledParagraph,
  SpinnerContainer,
  FranceLimitedRow,
  ProductTraitsRow,
  FranceSpecificSection,
} from './AgronomicPreferences.styles';
import { AgronomicWeightingsList } from './AgronomicWeightingsList';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { getTraitLabel, clearfieldTraitValues } from 'utils/trait';
import breakpoints from 'base/constants/breakpoints';
import {
  AgroPreferenceTitle,
  AgronomicPreferenceContent,
  SectionContainer,
  Text,
} from 'pages/RecommendationV2/V2Common.styles';
import { AgronomicInputs } from 'base/types/RecommendationCropwise';
import { StyledSpinner } from 'components/Spinner/Spinner.styles';
import { CropConstants } from 'utils/constants/Crop';
import { SS_AG_InputOptions } from 'utils/constants/AGInputOptionsConstants';
import { getFranceStyleConfig } from 'utils/config/franceStyleConfig';

/**
 * AgronomicPreferences Component
 *
 * This component renders different layouts based on the country:
 *
 * FOR FRANCE + SUNFLOWER:
 * - Shows LIMITED agronomic preferences (only 3 fields: yield range, planting date, harvest date)
 * - Displays a separate "Product Traits" section with 4 fields
 * - Uses custom responsive layouts (FranceLimitedRow, ProductTraitsRow)
 * - Shows the agronomic weighting list (same as other countries)
 *
 * FOR ALL OTHER COUNTRIES (including France with non-sunflower crops):
 * - Shows FULL agronomic preferences (varies by crop type, up to 8+ fields)
 * - No separate Product Traits section
 * - Uses standard layouts (StyledRow)
 * - Shows the agronomic weighting list
 *
 * The styling configuration is controlled via getFranceStyleConfig() utility.
 */
export default function AgronomicPreferences({
  sectionRef,
  isBrazil = false,
}: {
  readonly sectionRef?: any;
  readonly isBrazil?: boolean;
}) {
  const {
    BETWEEN_1_TO_3_YEARS,
    PLOUGHING,
    Less_than_3_Years,
    Over_3_Years,
    Conventional_Tillage_Ploughing,
    Minimal_or_No_Tillage,
  } = SS_AG_InputOptions;
  const [t] = useTranslation();
  const {
    apiData: { recommendationCropwise, countryList },
  } = useAppState();
  const [agronomicInputs, setAgronomicInputs] = useState<AgronomicInputs>();
  const isDDG = recommendationCropwise?.source === 'DDG';
  const countryCode = recommendationCropwise?.country;
  const cropName = recommendationCropwise?.recommendations[0]?.crop_name;
  const franceStyleConfig = getFranceStyleConfig(countryCode, cropName);

  const getYieldRange = (yieldRange: string, cropName: string, country: string): string => {
    // Return empty string if no yield range provided
    if (!yieldRange) {
      return '-';
    }

    // United Kingdom logic (string check only)
    if (country === 'United Kingdom') {
      if (yieldRange === '>9.5' || yieldRange === '> 9.5') {
        return '>6';
      }
      if (yieldRange === '<9.5' || yieldRange === '< 9.5') {
        return '<6';
      }
      return yieldRange || '-';
    }

    // Find the country configuration
    const countryConfig = countryList?.find(
      (countryItem) =>
        countryItem.name?.toLowerCase() === country?.toLowerCase() ||
        countryItem.shortCode?.toLowerCase() === country?.toLowerCase()
    );

    if (!countryConfig) {
      return yieldRange || '-';
    }

    // Find crop configuration within country
    const cropConfig = countryConfig.crops?.find(
      (crop) => crop.CropName?.toLowerCase() === cropName?.toLowerCase()
    );

    if (country === 'Brazil') {
      return `${agronomicInputs?.userSelectedRange || yieldRange} ${t(
        agronomicInputs?.extraInfo?.yieldRangeUnit || 'sacos/ha'
      )}`;
    }
    // Get yield range list for validation
    const yieldList = cropConfig?.YieldRange || [];

    // Check if current yield range exists in the yield list
    if (yieldRange && yieldList.length > 0) {
      const yieldRangeItem = yieldList.find((item) => item.id === yieldRange);

      if (yieldRangeItem) {
        // Return label if it exists, otherwise return the id
        return yieldRangeItem.label || yieldRangeItem.id;
      }

      // If yield range doesn't exist, apply transformations based on country/crop
      if (yieldRange.includes('-')) {
        const [firstPart, secondPart] = yieldRange.split('-').map(Number);

        // Bulgaria specific logic - divide the values
        if (countryConfig.shortCode === 'BGR') {
          const cropId = recommendationCropwise?.recommendations?.[0]?.crop_id;
          const divider = Number(cropId) === 2 ? 10 : 100; // Sunflower = 10, Corn = 100

          const dividedRange = `${firstPart / divider}-${secondPart / divider}`;
          const dividedItem = yieldList.find((item) => item.id === dividedRange);

          if (dividedItem) {
            // Return label if it exists, otherwise return the id
            return dividedItem.label || dividedItem.id;
          }
        }

        // Romania and other countries - multiply by 10
        const multipliedRange = `${firstPart * 10}-${secondPart * 10}`;
        const multipliedItem = yieldList.find((item) => item.id === multipliedRange);

        if (multipliedItem) {
          // Return label if it exists, otherwise return the id
          return multipliedItem.label || multipliedItem.id;
        }
      }
    }

    // Return original if no transformation worked
    return yieldRange || '-';
  };
  useEffect(() => {
    if (recommendationCropwise?.recommendations?.length) {
      const agroInput =
        recommendationCropwise.recommendations[0]?.multi_field?.config?.agronomicInputs;
      setAgronomicInputs(agroInput);
    }
  }, [recommendationCropwise]);

  useEffect(() => {
    let minH1Height = 30;
    let minP1Height = 20;
    const h1Tags = document.querySelectorAll('.ag-input h1');
    const p1Tags = document.querySelectorAll('.ag-input div');
    if (window.innerWidth <= parseInt(breakpoints.size.sm)) {
      p1Tags?.forEach((p: any) => {
        p.style.height = `25px`;
      });
      h1Tags?.forEach((h: any) => {
        h.style.minHeight = '40px';
      });
    }

    h1Tags.forEach((h1) => {
      const height = h1.clientHeight;
      if (height > minH1Height) {
        minH1Height = height;
      }
    });
    h1Tags?.forEach((h: any) => {
      h.style.minHeight = `${minH1Height}px`;
    });

    p1Tags.forEach((p1) => {
      const height = p1.clientHeight;
      if (height > minP1Height) {
        minP1Height = height;
      }
    });
    p1Tags?.forEach((p: any) => {
      p.style.height = `${minP1Height}px`;
    });
  }, []);

  const commonPreference = () => {
    return (
      <>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          {isBrazil === true ? (
            <StyledParagraph>{`${t('Historical yield')}`}</StyledParagraph>
          ) : (
            <StyledParagraph>{`${t('Historical yield range')}`}</StyledParagraph>
          )}
          <StyledH1>
            {getYieldRange(
              recommendationCropwise?.recommendations[0]?.multi_field?.config?.yieldRange ?? '',
              agronomicInputs?.cropName ?? '',
              recommendationCropwise?.country ?? ''
            ) ?? '-'}
          </StyledH1>
        </StyledCol>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph>{t('Targeted planting date')}</StyledParagraph>
          <StyledH1>
            {agronomicInputs?.plantingDate
              ? moment(agronomicInputs?.plantingDate).format('DD/MM/YY')
              : '-'}
          </StyledH1>
        </StyledCol>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph>{t('Targeted harvest date')}</StyledParagraph>
          <StyledH1>
            {agronomicInputs?.harvestDate
              ? moment(agronomicInputs?.harvestDate).format('DD/MM/YY')
              : '-'}
          </StyledH1>
        </StyledCol>
        {isBrazil === true && (
          <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
            <StyledParagraph>{t('Maturity Group')}</StyledParagraph>
            <StyledH1>
              {agronomicInputs?.maturityGroup ? agronomicInputs?.maturityGroup : '-'}
            </StyledH1>
          </StyledCol>
        )}
      </>
    );
  };

  // Limited preferences for France + Sunflower
  const franceLimitedPreference = () => {
    return (
      <FranceLimitedRow>
        <StyledCol className="ag-input france-item">
          <StyledParagraph>{`${t('Historical yield range')}`}</StyledParagraph>
          <StyledH1>
            {getYieldRange(
              recommendationCropwise?.recommendations[0]?.multi_field?.config?.yieldRange ?? '',
              agronomicInputs?.cropName ?? '',
              recommendationCropwise?.country ?? ''
            ) ?? '-'}
          </StyledH1>
        </StyledCol>
        <StyledCol className="ag-input france-item">
          <StyledParagraph>{t('Targeted planting date')}</StyledParagraph>
          <StyledH1>
            {agronomicInputs?.plantingDate
              ? moment(agronomicInputs?.plantingDate).format('DD/MM/YY')
              : '-'}
          </StyledH1>
        </StyledCol>
        <StyledCol className="ag-input france-item">
          <StyledParagraph>{t('Targeted harvest date')}</StyledParagraph>
          <StyledH1>
            {agronomicInputs?.harvestDate
              ? moment(agronomicInputs?.harvestDate).format('DD/MM/YY')
              : '-'}
          </StyledH1>
        </StyledCol>
      </FranceLimitedRow>
    );
  };

  // Product traits section for France + Sunflower
  const renderProductTraits = () => {
    const updatedTraitText = (value: string): string => {
      if (value !== clearfieldTraitValues[1]) {
        return getTraitLabel(value);
      }
      return '';
    };

    return (
      <FranceSpecificSection>
        <AgroPreferenceTitle>{t('Your Product Traits')}</AgroPreferenceTitle>
        <Text center>
          {t(
            'Below you can find a summary of traits for your recommendations based of your management practice and weed control.'
          )}
        </Text>
        <ProductTraitsRow style={{ paddingTop: '32px' }}>
          <StyledCol className="ag-input product-trait-item">
            <StyledParagraph>{t('Oil Trait')}</StyledParagraph>
            <StyledH1 isLongString>
              {agronomicInputs?.cropSegments?.length
                ? agronomicInputs?.cropSegments?.map((segmentText) => t(segmentText)).join(', ')
                : '-'}
            </StyledH1>
          </StyledCol>
          <StyledCol className="ag-input product-trait-item">
            <StyledParagraph>{t('Downy Mildew')}</StyledParagraph>
            <StyledH1>
              {(agronomicInputs?.extended_attributes as any)?.isDownyMildewObserved !== undefined
                ? (agronomicInputs?.extended_attributes as any)?.isDownyMildewObserved
                  ? t('Yes')
                  : t('No')
                : '-'}
            </StyledH1>
          </StyledCol>
          <StyledCol className="ag-input product-trait-item">
            <StyledParagraph>{t('Verticillium Variety Resistance')}</StyledParagraph>
            <StyledH1>
              {(agronomicInputs?.extended_attributes as any)?.verticilliumResistant
                ? t((agronomicInputs?.extended_attributes as any)?.verticilliumResistant)
                : '-'}
            </StyledH1>
          </StyledCol>
          <StyledCol className="ag-input product-trait-item">
            <StyledParagraph>{t('Herbicide Technology')}</StyledParagraph>
            <StyledH1 isLongString lang="en">
              {agronomicInputs?.traits?.length
                ? agronomicInputs?.traits
                  ?.map((traitText) => t(`${updatedTraitText(traitText)}`))
                  .filter((v) => v !== '')
                  .join(', ')
                : '-'}
            </StyledH1>
          </StyledCol>
        </ProductTraitsRow>
      </FranceSpecificSection>
    );
  };

  const renderCorn = () => {
    const isBarley =
      recommendationCropwise?.recommendations[0]?.crop_name
        .toLowerCase()
        .includes(CropConstants.BARLEY) ?? false;
    return (
      <StyledRow>
        {!isBarley && !isBrazil && (
          <StyledCol span={4} xs={24} sm={12} lg={6} className="ag-input">
            <StyledParagraph className="ag-input-values">
              {t('Product maturity range (FAO)')}
            </StyledParagraph>
            <StyledH1>
              {agronomicInputs?.fao?.[0]
                ? `${agronomicInputs?.fao[0]}-${agronomicInputs?.fao[1]}`
                : '-'}
            </StyledH1>
          </StyledCol>
        )}
        {commonPreference()}
      </StyledRow>
    );
  };

  const renderWheat = () => {
    return <StyledRow>{commonPreference()}</StyledRow>;
  };

  const renderBarley = () => {
    const isBYDV = agronomicInputs?.extended_attributes?.barleyYellowDwarfVirus;
    const isBaYMV2 = agronomicInputs?.extended_attributes?.barleyYellowMosaicVirus;

    return (
      <StyledRow>
        {commonPreference()}
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph className="ag-input-values">
            {t('Resistant Variety Required')}
          </StyledParagraph>
          {isBYDV && isBaYMV2 ? (
            <StyledH1>{'BYDV & BaYMV-2'}</StyledH1>
          ) : isBYDV ? (
            <StyledH1>{'BYDV'}</StyledH1>
          ) : isBaYMV2 ? (
            <StyledH1>{'BaYMV-2'}</StyledH1>
          ) : (
            <StyledH1>{'-'}</StyledH1>
          )}
        </StyledCol>
      </StyledRow>
    );
  };

  const updatedTraitText = (value: string): string => {
    // if (value !== clearfieldTraitValues[1]) {
    //   return getTraitLabel(value);
    // }
    return getTraitLabel(value);
  };

  const getRotationText = (value: string): string => {
    return value === BETWEEN_1_TO_3_YEARS ? t(Less_than_3_Years) : t(Over_3_Years);
  };

  const getTillagePracticeText = (value: string): string => {
    return value === PLOUGHING ? t(Conventional_Tillage_Ploughing) : t(Minimal_or_No_Tillage);
  };

  const renderSunflower = () => {
    const tillageTextAdjusted = agronomicInputs?.tillage
      ? t(agronomicInputs.tillage).split('/').join(' / ')
      : '-';
    return (
      <StyledRow>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph isLongString>{`${t('Historical yield range')}`}</StyledParagraph>
          <StyledH1>
            {getYieldRange(
              recommendationCropwise?.recommendations[0]?.multi_field?.config?.yieldRange ?? '',
              agronomicInputs?.cropName ?? '',
              recommendationCropwise?.country ?? ''
            ) ?? '-'}
          </StyledH1>
        </StyledCol>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph isLongString>{t('Targeted planting date')}</StyledParagraph>
          <StyledH1>
            {agronomicInputs?.plantingDate
              ? moment(agronomicInputs?.plantingDate).format('DD/MM/YY')
              : '-'}
          </StyledH1>
        </StyledCol>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph isLongString>{t('Targeted harvest date')}</StyledParagraph>
          <StyledH1>
            {agronomicInputs?.harvestDate
              ? moment(agronomicInputs?.harvestDate).format('DD/MM/YY')
              : '-'}
          </StyledH1>
        </StyledCol>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph>{t('Oil Trait')}</StyledParagraph>
          <StyledH1 isLongString>
            {agronomicInputs?.cropSegments?.length
              ? agronomicInputs?.cropSegments?.map((segmentText) => t(segmentText)).join(', ')
              : '-'}
          </StyledH1>
        </StyledCol>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph>{t('Broomrape Technology')}</StyledParagraph>
          {isDDG ? (
            <StyledH1>
              {agronomicInputs?.broomRape?.length
                ? agronomicInputs?.broomRape?.map((broomRape) => t(broomRape)).join(', ')
                : '-'}
            </StyledH1>
          ) : (
            // as discussed with Evgeniya and Balu we will show first broomrape technology in nonDDG recommendation
            // Update: if 'S' is the first broomrape technology, show '-' instead for non-DDG recs
            <StyledH1>
              {' '}
              {agronomicInputs?.broomRape?.length
                ? agronomicInputs?.broomRape[0] === 'S'
                  ? '-'
                  : agronomicInputs?.broomRape[0]
                : '-'}
            </StyledH1>
          )}
        </StyledCol>
        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph>{t('Herbicide Technology')}</StyledParagraph>
          <StyledH1 isLongString lang="en">
            {agronomicInputs?.traits?.length
              ? agronomicInputs?.traits
                ?.map((traitText) => t(`${updatedTraitText(traitText)}`))
                .filter((v) => v !== '')
                .join(', ')
              : '-'}
          </StyledH1>
        </StyledCol>

        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph>{t('Sunflower Rotation')}</StyledParagraph>
          {isDDG ? (
            <StyledH1>{agronomicInputs?.rotation ? t(agronomicInputs.rotation) : '-'}</StyledH1>
          ) : (
            <StyledH1>
              {' '}
              {agronomicInputs?.inputManagementPractices?.crop_rotation
                ? getRotationText(agronomicInputs.inputManagementPractices.crop_rotation)
                : '-'}
            </StyledH1>
          )}
        </StyledCol>

        <StyledCol span={6} xs={24} sm={12} lg={6} className="ag-input">
          <StyledParagraph>{t('Tillage Practice')}</StyledParagraph>
          {isDDG ? (
            <StyledH1>{tillageTextAdjusted}</StyledH1>
          ) : (
            <StyledH1>
              {' '}
              {agronomicInputs?.inputManagementPractices?.tillage_system
                ? getTillagePracticeText(agronomicInputs.inputManagementPractices.tillage_system)
                : '-'}
            </StyledH1>
          )}
        </StyledCol>
      </StyledRow>
    );
  };

  const renderCrop = () => {
    // For France + Sunflower, show limited preferences (3 fields only)
    if (franceStyleConfig.useLimitedAgronomicPreferences) {
      return franceLimitedPreference();
    }

    const cropName = recommendationCropwise?.recommendations[0]?.crop_name.toLowerCase();
    switch (true) {
      case cropName?.includes('corn'):
        return renderCorn();
      case cropName?.includes('sunflower'):
        return renderSunflower();
      case cropName?.includes('wheat'):
        return renderWheat();
      case cropName?.includes('barley'):
        return renderBarley();
      default:
        return renderCorn();
    }
  };

  return (
    <SectionContainer
      ref={sectionRef}
      data-testid="agronomic-preference-section"
      className="secondary-container"
    >
      <AgronomicPreferenceContent>
        <AgroPreferenceTitle>{t('Your Agronomic Preferences')}</AgroPreferenceTitle>
        <Text center>{t('Your Agronomic Preferences description')}</Text>
        {!agronomicInputs ? (
          <SpinnerContainer>
            <StyledSpinner />
          </SpinnerContainer>
        ) : (
          <AgronomicInputsContainer id="ag-preference-input">
            {renderCrop()}
          </AgronomicInputsContainer>
        )}
        {/* Show Product Traits section for France + Sunflower */}
        {franceStyleConfig.showProductTraitsSection && agronomicInputs && renderProductTraits()}
        {!isBrazil && <AgronomicWeightingsList />}
      </AgronomicPreferenceContent>
    </SectionContainer>
  );
}
